import React, {useState, useCallback, useEffect, useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {cn} from '@/lib/utils';
import {Trophy, X} from 'lucide-react';
import {User} from '../Auth/User';
import AthenaAPI from '../APIs/AthenaAPI';
import {TIME_PERIOD_KEYS, METRIC_KEYS, toAgentData} from './data';
import type {AgentData} from './data';
import LeaderboardPopout from './LeaderboardPopout';
import type {MetricType, TimePeriodType} from './LeaderboardPopout';

/* ─── localStorage helpers ─── */

const LS_KEYS = {
    open: 'leaderboard-widget-open',
    metric: 'leaderboard-widget-metric',
    period: 'leaderboard-widget-period',
} as const;

function readLS<T>(key: string, fallback: T): T {
    try {
        const val = localStorage.getItem(key);
        if (val === null) return fallback;
        return val as unknown as T;
    } catch {
        return fallback;
    }
}

function writeLS(key: string, value: string) {
    try {
        localStorage.setItem(key, value);
    } catch {
        // localStorage unavailable — silently ignore
    }
}

/* ─── Widget ─── */

export default function LeaderboardWidget() {
    // State — initialized from localStorage
    const [isOpen, setIsOpen] = useState(() => readLS<string>(LS_KEYS.open, 'false') === 'true');
    const [isClosing, setIsClosing] = useState(false);
    const [metric, setMetricState] = useState<MetricType>(() => readLS<MetricType>(LS_KEYS.metric, 'submitted'));
    const [timePeriod, setTimePeriodState] = useState<TimePeriodType>(() =>
        readLS<TimePeriodType>(LS_KEYS.period, 'daily'),
    );

    // Persist to localStorage on change
    const setMetric = useCallback((m: MetricType) => {
        setMetricState(m);
        writeLS(LS_KEYS.metric, m);
    }, []);

    const setTimePeriod = useCallback((t: TimePeriodType) => {
        setTimePeriodState(t);
        writeLS(LS_KEYS.period, t);
    }, []);

    const handleToggle = useCallback(() => {
        if (isOpen) {
            setIsClosing(true);
        } else {
            setIsOpen(true);
            writeLS(LS_KEYS.open, 'true');
        }
    }, [isOpen]);

    const handleAnimationEnd = useCallback(() => {
        if (isClosing) {
            setIsClosing(false);
            setIsOpen(false);
            writeLS(LS_KEYS.open, 'false');
        }
    }, [isClosing]);

    // Sync open state across browser tabs via storage events
    useEffect(() => {
        const handleStorage = (e: StorageEvent) => {
            if (e.key === LS_KEYS.open) {
                setIsOpen(e.newValue === 'true');
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    /* ─── Data fetching ─── */

    const {data: leaderboardData, isLoading} = useQuery({
        queryKey: ['leaderboard'],
        queryFn: () => AthenaAPI.getLeaderboard(),
        refetchInterval: 60000,
        refetchOnWindowFocus: false,
    });

    /* ─── Transform API data for the current tab selection ─── */

    const {agents, currentAgent} = useMemo(() => {
        if (!leaderboardData?.data) return {agents: [] as AgentData[], currentAgent: null as AgentData | null};

        const periodKey = TIME_PERIOD_KEYS[timePeriod];
        const metricKey = METRIC_KEYS[metric];
        const entries = leaderboardData.data[periodKey]?.[metricKey] ?? [];

        const allAgents = entries.map((entry, i) => toAgentData(entry, i + 1));

        // Find current user by case-insensitive name match
        const userName = User.Info.name?.toLowerCase() ?? '';
        const userInList = userName ? allAgents.find((a) => a.name.toLowerCase() === userName) : null;

        // If user is in top 10, they're shown inline — no separate currentAgent
        // If user is ranked > 10, show in "Your Position" footer
        const current = userInList && userInList.rank > 10 ? userInList : null;

        return {agents: allAgents, currentAgent: current};
    }, [leaderboardData, timePeriod, metric]);

    return (
        <>
            {/* ── Popout Panel ── */}
            {(isOpen || isClosing) && (
                <div
                    className={cn(
                        'fixed bottom-20 right-6 z-[101] h-[600px] w-[400px] overflow-hidden rounded-2xl border border-stone-200/80 shadow-xl',
                        isOpen && !isClosing && 'animate-in fade-in-0 zoom-in-95 duration-200 ease-out',
                        isClosing && 'animate-out fade-out-0 zoom-out-95 duration-150 ease-in',
                    )}
                    style={{
                        transformOrigin: 'bottom right',
                        background: 'linear-gradient(135deg, #f8faf9 0%, #f0f4f2 40%, #f8faf9 100%)',
                    }}
                    onAnimationEnd={handleAnimationEnd}
                >
                    {/* Dot-grid texture */}
                    <div
                        className="pointer-events-none absolute inset-0 z-0 opacity-[0.25]"
                        style={{
                            backgroundImage: 'radial-gradient(circle, rgba(69,109,105,0.15) 1px, transparent 1px)',
                            backgroundSize: '20px 20px',
                        }}
                    />

                    {/* Warm spotlight glow at top */}
                    <div
                        className="pointer-events-none absolute -top-10 left-1/2 z-0 h-[200px] w-[300px] -translate-x-1/2 rounded-full"
                        style={{
                            background: 'radial-gradient(circle, rgba(69,109,105,0.04) 0%, transparent 70%)',
                        }}
                    />

                    {/* Content */}
                    <div className="relative z-10 h-full overflow-y-auto">
                        <LeaderboardPopout
                            metric={metric}
                            setMetric={setMetric}
                            timePeriod={timePeriod}
                            setTimePeriod={setTimePeriod}
                            agents={agents}
                            currentAgent={currentAgent}
                            currentUserName={User.Info.name ?? null}
                            isLoading={isLoading}
                            updatedAt={leaderboardData?.updatedAt}
                        />
                    </div>
                </div>
            )}

            {/* ── FAB Button ── */}
            <button
                onClick={handleToggle}
                className={cn(
                    'fixed bottom-6 right-6 z-[100] flex h-12 w-12 items-center justify-center rounded-xl shadow-lg transition-all duration-200',
                    'hover:scale-105 hover:shadow-xl active:scale-95',
                    'bg-[#456d69]',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#456d69]/50 focus-visible:ring-offset-2',
                )}
                style={{
                    boxShadow: isOpen
                        ? '0 0 0 2px rgba(69,109,105,0.3), 0 10px 25px -5px rgba(69,109,105,0.3)'
                        : '0 10px 25px -5px rgba(69,109,105,0.25)',
                }}
                aria-label={isOpen ? 'Close sales leaderboard' : 'Open sales leaderboard'}
                aria-expanded={isOpen}
            >
                {isOpen ? <X className="h-5 w-5 text-white" /> : <Trophy className="h-5 w-5 text-white" />}
            </button>
        </>
    );
}
